import { compact, find, flow, isEmpty, reduce, set } from 'lodash/fp';

import { CMS_CONTENT_SECTIONS, CONTENT_SECTIONS } from '../../content/sections';
import getAllQuestions from '../get-all-questions';
import getFormattedQuestionForAccordion from '../../../../utils/get-formatted-question-for-accordion';
import getMetaData from '../get-meta-data';
import mapAddOnsContent from './map-add-ons-content';
import mapAwardsContent from './map-awards-content';
import mapCommonQuestionsAlternateContent from './map-common-questions-alternate-content';
import mapCommonQuestionsContent from './map-common-questions-content';
import mapContactDetailsContent from './map-contact-details-content';
import mapCrossSellContent from './map-cross-sell-content';
import mapFeaturesAndBenefits from './map-features-and-benefits';
import mapFormsAndGuidesContent from './map-forms-and-guides-content';
import mapHeaderContent from './map-header-content';
import mapOffer from './map-offer';
import mapProductContent from './map-products-content';
import mapPromotions from './map-promotions';
import mapQuickLinks from './map-quick-links';
import mapTermsAndConditionsContent from '../../../../utils/map-terms-and-conditions-content';
import mapTiles from './map-tiles';
import mapValuePropositionsContent from './map-value-propositions-content';
import uncapFpIterator from '../../../../../utils/uncap-fp-iterator';
import type { CmsInsuranceTypeContent, InsuranceTypeContent, Section, StaticInsuranceTypeContent } from '../../types';

const reduceWithKeys = uncapFpIterator(reduce);

type CombineContentWithCmsDataProps = {
  cmsData: Partial<CmsInsuranceTypeContent> | null;
  staticContent: StaticInsuranceTypeContent;
};

const combineContentWithCmsData = ({
  cmsData,
  staticContent,
}: CombineContentWithCmsDataProps): InsuranceTypeContent => {
  const mergedData = {
    ...staticContent,
    ...cmsData,
  };

  if (isEmpty(mergedData)) {
    return { sections: [] };
  }

  const insuranceType = cmsData?.identifier?.current;
  const insuranceName = cmsData?.name;

  const formattedSections = reduceWithKeys(
    (result: InsuranceTypeContent['sections'], section: string, key: string) => {
      const staticSectionContent = find({ type: section }, staticContent?.sections);
      // @ts-expect-error - Automatic, Please fix when editing this file
      const cmsSectionContent = cmsData?.[CMS_CONTENT_SECTIONS[key]];
      const offerData = cmsData?.offer;
      const hasTargetMarket = cmsData?.marketSegment;

      let sectionContent: unknown;
      switch (section) {
        case CONTENT_SECTIONS.HEADER:
          sectionContent = mapHeaderContent({ cmsSectionContent, insuranceType, offerData });
          break;

        case CONTENT_SECTIONS.PROMOTIONS:
          sectionContent = mapPromotions({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.OFFER_STRIP:
          sectionContent = mapOffer({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.VALUE_PROPOSITIONS:
          sectionContent = mapValuePropositionsContent({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.FEATURES_AND_BENEFITS:
          sectionContent = mapFeaturesAndBenefits({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.FORMS_AND_GUIDES:
          sectionContent = mapFormsAndGuidesContent({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.CONTACT_DETAILS:
          sectionContent = mapContactDetailsContent({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.TILES:
          sectionContent = mapTiles({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.COMMON_QUESTIONS:
          sectionContent = mapCommonQuestionsContent({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.COMMON_QUESTIONS_ALTERNATE:
          sectionContent = mapCommonQuestionsAlternateContent({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.TERMS_AND_CONDITIONS:
          sectionContent = mapTermsAndConditionsContent({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.QUICK_LINKS:
          sectionContent = mapQuickLinks({ cmsSectionContent });
          break;

        case CONTENT_SECTIONS.PRODUCTS:
          sectionContent = mapProductContent({ cmsSectionContent, hasTargetMarket });
          break;

        case CONTENT_SECTIONS.CROSS_SELL:
          sectionContent = mapCrossSellContent({ cmsSectionContent, insuranceType: insuranceName });
          break;

        case CONTENT_SECTIONS.ADD_ONS:
        case CONTENT_SECTIONS.RELEVANT_ADD_ONS:
          sectionContent = mapAddOnsContent({ cmsSectionContent });
          break;

        default:
          sectionContent = staticSectionContent;
      }

      // @ts-expect-error - Automatic, Please fix when editing this file
      return [...result, sectionContent];
    },
    [],
    CONTENT_SECTIONS,
  );

  const metaData = getMetaData(mergedData, staticContent);

  const awardTiles = mergedData?.award;
  const filteredSections: Section[] = compact(formattedSections);
  const allQuestions = getFormattedQuestionForAccordion(getAllQuestions({ cmsData: mergedData }));
  const sectionsWithAwardTiles = mapAwardsContent({ awards: awardTiles || null, sections: filteredSections });
  const mergedContent = flow(
    set('sections', sectionsWithAwardTiles),
    set('metadataObject', metaData),
    set('faqQuestions', allQuestions),
  )(mergedData);

  return mergedContent;
};

export default combineContentWithCmsData;
